import type * as zod from 'zod';

import { FailureKind } from '../../FailureKind';
import type { Routine } from '../../Routine';
import { RoutineFrame } from '../../RoutineFrame';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';
import type { StepValidationMessage } from '../StepValidationMessage';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

/**
 * Dispatch-time recursion guard. Loads from trusted callers are acyclic
 * (their graph walks detect cycles), but the wire `loadRoutine` packet
 * accepts an arbitrary subroutines record — a cyclic record must fail a
 * step cleanly instead of exhausting the stack mid-motion. Scope keys
 * nest one segment per call, so segment count is call depth.
 */
const MAX_SUBROUTINE_CALL_DEPTH = 16;

export default class CallSubroutineStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {};
  }

  public async _play({ playSubSteps, fail }: StepPlayArguments): Promise<void> {
    if (
      this.routineFrame.scopeKey.split(':').length > MAX_SUBROUTINE_CALL_DEPTH
    ) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: this.getStepKind(),
        },
        failureReason:
          `Subroutine call depth exceeded ${MAX_SUBROUTINE_CALL_DEPTH} — ` +
          'check for a subroutine cycle',
      });
    }

    const subRoutine = this.routineContext.lookupSubroutine(
      this.args.routineID,
    );

    if (!subRoutine) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CallSubroutine',
        },
        failureReason: `Subroutine "${this.args.routineID}" was not loaded`,
      });
    }

    // Allocate a fresh frame for this invocation. Each invocation gets its
    // own variable bag and local-space copy so concurrent / re-entrant calls
    // don't share state.
    const frame = new RoutineFrame({
      loadedRoutine: this.routineFrame.loadedRoutine,
      scopeKey: `${this.routineFrame.scopeKey}:${this.id}`,
      routineId: subRoutine.id,
      routineName: subRoutine.name,
      callSubroutineStepId: this.id,
      // The calling step's frame. `LoadedRoutine.deriveStepFailureFields`
      // walks back up via `parentFrame` to find the loaded-routine
      // `CallSubroutine` that started the chain, populating
      // `FailureState.failedStepLoadedRoutineStepId` (R11).
      parentFrame: this.routineFrame,
      // Deep-clone the spec's `space` so each invocation owns its own
      // `Space.Item` objects. A shallow copy would share item references
      // across calls and with the spec itself — and writes like
      // `setPositionListEntries` mutate item contents (`spaceItem.positions
      // = ...`), which would leak across concurrent calls and persist
      // through `routineContext.subroutinesById`.
      initialSpace: structuredClone(subRoutine.space ?? []),
    });

    await this.seedEnvironmentVariables(frame, subRoutine);

    const construct = this.constructSubSteps(frame, subRoutine);

    if ('errors' in construct) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CallSubroutine',
        },
        failureReason: `Subroutine "${this.args.routineID}" construction failed: ${JSON.stringify(construct.errors)}`,
      });
    }

    // Expose the per-call sub tree on the sub's frame so a failure inside
    // the sub can resolve its 1-based DFS step position via
    // `step.getStepPosition()` for R11 failure attribution. Positions are
    // per-routine, so each call resolves the same number for the same step
    // id.
    frame.routineSteps = construct.steps;

    // CallSubroutine is unique: every other substep-dispatching step uses
    // its spec-bound `this.substeps` set once at construction. We build a
    // fresh per-call tree above (new frame, new instances), so we pass it
    // through playSubSteps's `substeps` arg-bag override instead. The shared
    // CallSubroutineStep instance stays stateless across calls — re-entrant
    // dispatches (recursion, parallel callers) don't trample each other.
    // `skipSettingCurrentStepID` is deliberately NOT passed: absent keys
    // inherit, so a main-track call publishes its sub steps as the current
    // step while a background (RunInBackground) call keeps its inherited
    // skip and stays off the wire. An explicit value here would override
    // the inheritance for both.
    //
    // Visualizations written by the sub are NOT cleared on return: viz
    // persists for the whole run. Sub-local overlays linger harmlessly
    // (geometry still resolves from the loaded subroutine spec); global
    // writes are shared and any scope can later unset them. The run boundary
    // (stop → Idle in `RoutineRunner`) is where non-loaded-routine overlays
    // are reset.
    await playSubSteps({
      substeps: construct.steps,
      // Explicitly severed: a subroutine call is a routine boundary, so a
      // LoopControl Break/Continue inside the sub must bind to a loop
      // INSIDE the sub (or fail as "not in a loop") — never to a Loop in
      // the calling routine. `playSubSteps` spreads these LAST over the
      // inherited values, so the explicit `undefined`s override the
      // caller's controls; deleting these lines would reinstate
      // inheritance.
      breakOutOfLoop: undefined,
      continueLoop: undefined,
    });
  }

  /**
   * Seed subroutine-local environment variables onto the per-call frame.
   *
   * Persistent values come from `routineContext.persistentValuesByRoutineId`,
   * pre-populated by `RoutineRunner.playRoutine` for every reachable
   * subroutine id. On a cache miss, fall back to `initialValue`. For
   * non-persistent vars, `initialValue` is evaluated on the sub frame so
   * per-call expressions see this invocation's variable scope.
   *
   * Workspace globals (`isGlobal: true`) are skipped — they're seeded
   * once at `loadRoutine` and visible through the frame's layered lookup.
   */
  private async seedEnvironmentVariables(
    frame: RoutineFrame,
    subRoutine: Routine,
  ): Promise<void> {
    for (const envVar of subRoutine.environmentVariables ?? []) {
      if (envVar.isGlobal) continue;

      let value: unknown = null;

      if (envVar.persistent) {
        const persistent = this.routineContext.persistentValuesByRoutineId.get(
          subRoutine.id,
        );

        if (persistent?.has(envVar.id)) {
          value = persistent.get(envVar.id);
        } else if (envVar.initialValue) {
          value = await frame.evaluateExpression(envVar.initialValue);
        }
      } else if (envVar.initialValue) {
        value = await frame.evaluateExpression(envVar.initialValue);
      }

      frame.setVariable(envVar.id, {
        stepKind: 'EnvironmentVariable',
        value,
        name: envVar.name,
        isGlobal: false,
        persistent: envVar.persistent ?? false,
      });
    }
  }

  /**
   * Construct the per-call substep tree against the given frame.
   *
   * `constructStepFromCache` reads parsed args from the cache populated at
   * load-time validation, so it skips Zod re-parsing and the per-walk
   * duplicate-id guard.
   */
  private constructSubSteps(
    frame: RoutineFrame,
    subRoutine: Routine,
  ): { steps: Step[] } | { errors: StepValidationMessage[] } {
    const subSteps: Step[] = [];
    let errors: StepValidationMessage[] | null = null;

    for (const stepConfig of subRoutine.steps) {
      const result = Step.constructStepFromCache(
        stepConfig,
        this.routineContext,
        frame,
      );

      if ('errors' in result) {
        errors = result.errors;
        break;
      }

      subSteps.push(result.step);
    }

    if (errors) {
      return { errors };
    }

    return { steps: subSteps };
  }
}
